import React, { useCallback, useEffect, useId, useMemo, useState } from 'react'
import UploadArea from 'components/utility/upload_area'
import clsx from 'clsx'
import axios from 'axios'

const PRODUCT_TYPES = {
  None: {
    label: "None",
    hasAdditionalFields: false,
    baseType: null
  },
  Application: {
    label: "Application",
    hasAdditionalFields: true,
    baseType: 'package_machine',
    additionalFields: [
      {
        type: 'package_machine',
        label: 'package machine',
        required: true,
        endpoint: '/super/application_form_base_package_machines'
      }
    ]
  },
  Questionnaire: {
    label: "Questionnaire",
    hasAdditionalFields: true,
    baseType: 'questionnaire_machine',
    additionalFields: [
      {
        type: 'questionnaire_machine',
        label: 'questionnaire machine',
        required: true,
        endpoint: '/super/application_form_base_questionnaire_machines'
      }
    ]
  }
  // Future product types can be added here
}

const InputField = ({ label, value, onChange, ...props }) => {
  const inputId = useId()
  return (
    <div className="dome-d-flex dome-gap3 dome-flex-column">
      <label htmlFor={inputId} className="dome-p3 dome-text-w400 dome-color-med-grey">
        {label}
      </label>
      <input id={inputId} value={value} onChange={onChange} {...props} />
    </div>
  )
}

const SelectField = ({ label, name, options, value, onChange }) => {
  return (
    <div className="dome-d-flex dome-gap3 dome-flex-column">
      <label className="dome-p3 dome-text-w400 dome-color-med-grey">{label}</label>
      <select value={value} onChange={onChange}>
        {options.map(({ id, label }) => (
          <option key={id} value={id}>
            {label}
          </option>
        ))}
      </select>
    </div>
  )
}

const DynamicField = ({ fieldConfig, value, onChange, loading, options }) => {
  const { type, label, required } = fieldConfig
  const fieldLabel = required ? `${label}*` : label

  return (
    <div className="dome-d-flex dome-gap3 dome-flex-column">
      <label className="dome-p3 dome-text-w400 dome-color-med-grey">{fieldLabel}</label>
      {loading ? (
        <div className="dome-d-flex dome-align-center dome-gap6">
          <i className="fa-duotone fa-spinner fa-spin-pulse"></i>
          <span className="dome-p3">Loading {label}...</span>
        </div>
      ) : options && options.length > 0 ? (
        <select
          value={value}
          onChange={onChange}
          // Add an accessibility attribute maybe?
          aria-label={fieldLabel}
        >
          {/* Consider adding a default placeholder option */}
          {/* <option value="">-- Select {label} --</option> */}
          {options.map(({ id, label: optionLabel }) => ( // Ensure inner label doesn't clash
            <option key={id} value={id}>
              {optionLabel}
            </option>
          ))}
        </select>
      ) : (
        <div className="dome-p3 dome-color-med-grey">No {label} available</div>
      )}
    </div>
  );
}

const FileDisplayArea = ({ file, onRemoveFile }) => {
  if (!file) return null
  const originalFileName = file?.name

  const formatFileSize = useMemo(() => {
    var size = file.size
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(size) / Math.log(k))

    return `${parseFloat((size / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
  }, [file])

  return (
    <div className="form-builder-uploaded-file dome-d-flex dome-justify-between dome-bg-fill">
      <div className="dome-d-flex dome-gap12 dome-align-center">
        <i className="fa-thin fa-magnifying-glass"></i>
        <span className="dome-p3 dome-color-darkest-grey">{originalFileName}</span>
        <span className="dome-p3 dome-color-darkest-grey">{formatFileSize}</span>
      </div>
      <span className="dome-color-red-alert dome-d-flex dome-align-center dome-cursor-pointer">
        <i onClick={onRemoveFile} className="fa-light fa-times delete-file-icon"></i>
      </span>
    </div>
  )
}

const CreatingFormBuilder = ({ organizations, hideOverlay }) => {
  const [file, setFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [formName, setFormName] = useState('')
  const [organizationId, setOrganizationId] = useState('')
  const [formType, setFormType] = useState('None')
  const [additionalFieldData, setAdditionalFieldData] = useState({})
  const [additionalFieldOptions, setAdditionalFieldOptions] = useState({})
  const [loadingAdditionalFields, setLoadingAdditionalFields] = useState({})
  const [error, setError] = useState(null)

  const organizationElements = useMemo(() => organizations.map(([label, id]) => ({ id, label })), [organizations])
  
  const formTypeOptions = useMemo(() =>
    Object.entries(PRODUCT_TYPES).map(([id, { label }]) => ({ id, label })),
    []
  )

  // Set the default value for organization
  useEffect(() => {
    if (organizationElements.length > 0) {
      setOrganizationId(organizationElements[0].id)
    }
  }, [organizationElements])

  // Reset additional fields when form type changes
  useEffect(() => {
    setAdditionalFieldData({})
    setAdditionalFieldOptions({})
    setLoadingAdditionalFields({})

    const selectedProductType = PRODUCT_TYPES[formType]

    if (selectedProductType && selectedProductType.hasAdditionalFields) {
      // Load data for all additional fields
      selectedProductType.additionalFields.forEach(field => {
        if (field.endpoint) {
          fetchAdditionalFieldOptions(field.type, field.endpoint)
        }
      })
    }
  }, [formType])

  const fetchAdditionalFieldOptions = async (fieldType, endpoint) => {
    setLoadingAdditionalFields(prev => ({ ...prev, [fieldType]: true }))
    setError(null)

    try {
      const response = await axios.get(endpoint, {
        headers: {
          'Accept': 'application/json'
        }
      })

      const data = Array.isArray(response.data) ? response.data :
                  Array.isArray(response.data?.[fieldType]) ? response.data[fieldType] : [];

      const options = data.map(item => ({
        id: item.id,
        label: item.label || `${fieldType} ${item.id}`
      }))

      setAdditionalFieldOptions(prev => ({ ...prev, [fieldType]: options }))

      if (options.length > 0 && options[0]?.id !== undefined) {
        setAdditionalFieldData(prev => ({ ...prev, [fieldType]: options[0].id }))
      } else {
        setAdditionalFieldData(prev => ({ ...prev, [fieldType]: '' }))
      }
    } catch (err) {
      setError(`Failed to load ${fieldType}. Please try again.`)
      setAdditionalFieldOptions(prev => ({ ...prev, [fieldType]: [] }))
      setAdditionalFieldData(prev => ({ ...prev, [fieldType]: '' }));
    } finally {
      setLoadingAdditionalFields(prev => ({ ...prev, [fieldType]: false }))
    }
  }

  const addFile = (newFile) => setFile(newFile[0])

  const removeFile = () => setFile(null)

  const handleAdditionalFieldChange = (fieldType, value) => {
    setAdditionalFieldData(prev => ({ ...prev, [fieldType]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null) // Reset error state before submission

    try {
      const form = { 
        name: formName, 
        organization_id: organizationId, 
        pdf_file: file.file,
        form_type: formType
      }

      // Add additional fields based on product type
      const selectedProductType = PRODUCT_TYPES[formType]
      if (selectedProductType?.baseType) {
        form['base_type'] = selectedProductType.baseType
      }

      if (selectedProductType && selectedProductType.hasAdditionalFields) {
        selectedProductType.additionalFields.forEach(field => {
          const fieldType = field.type;
          const fieldValue = additionalFieldData[fieldType]
          if (fieldValue) {
            form['base_id'] = fieldValue
          }
        })
      }

      const formData = new FormData()
      Object.entries(form).forEach(([key, value]) => {
        formData.append(`form[${key}]`, value)
      })

      const response = await axios.post('/forms/v3/', formData)
      await pollStatus(response.data.form_id)
    } catch (err) {
      console.error('Submission error:', err)
      setLoading(false)

      if (err.response) {
        if (err.response.status === 400) {
          setError('Invalid form data. Please check your inputs and try again.')
        } else {
          setError(`Server error: ${err.response.status} ${err.response.statusText}`)
        }
      } else if (err.request) {
        setError('No response from server. Please check your connection and try again.')
      } else {
        setError(`Error: ${err.message}`)
      }
    }
  }

  const pollStatus = async (formId) => {
    try {
      const { data } = await axios.get(`/forms/v3/upload_status/${formId}`)

      if (data.status && (data.status.state === 'failed' || data.status.state === 'failure')) {
        setError(data.status.error || 'Form processing failed')
        setLoading(false)
        return
      }

      handleStatus(data.status, formId, async () => {
        await new Promise(resolve => setTimeout(resolve, 2000))
        await pollStatus(formId)
      })
    } catch (err) {
      console.error('Polling error:', err)
      setLoading(false)

      if (err.response) {
        if (err.response.status === 400) {
          setError('Server returned an error while processing your form.')
        } else {
          setError(`Status check failed: ${err.response.status} ${err.response.statusText}`)
        }
      } else {
        setError('Failed to check upload status. Please verify if your form was created.')
      }
    }
  }

  const handleStatus = useCallback((status, formId, continuePoll) => {
    if (!status) {
      setError('Invalid status response received')
      setLoading(false)
      return
    }

    switch (status.state) {
      case 'failure':
        throw new Error(status.error)
      case 'failed':
        setError(status.error || 'Form processing failed')
        setLoading(false)
        break
      case 'complete':
        setLoading(false)
        window.location.replace(`/forms/v3/${formId}/form_builder`)
        break
      default:
        continuePoll()
        break
    }
  }, [])

  const isSubmitDisabled = () => {
    if (!file || !formName || !organizationId) {
      return true
    }

    // Check if all required additional fields are filled
    const selectedProductType = PRODUCT_TYPES[formType]
    if (selectedProductType && selectedProductType.hasAdditionalFields) {
      return selectedProductType.additionalFields
        .filter(field => field.required)
        .some(field => !additionalFieldData[field.type])
    }

    return false
  }

  return (
    <form onSubmit={handleSubmit} className="dome-d-flex dome-gap24 dome-flex-column">
      <div className="dome-h2 dome-color-dark-green dome-text-w500">create new form</div>

      {error && (
        <div className="dome-p3 dome-color-red-alert dome-bg-light-red dome-p12 dome-rounded">
          <i className="fa-light fa-circle-exclamation dome-mr6"></i>
          {error}
        </div>
      )}

      <InputField label="form name*" value={formName} onChange={(e) => setFormName(e.target.value)} />

      <SelectField
        label="organization name*"
        value={organizationId}
        options={organizationElements}
        onChange={(e) => setOrganizationId(e.target.value)}
      />

      <SelectField
        label="form type*"
        value={formType}
        options={formTypeOptions}
        onChange={(e) => setFormType(e.target.value)}
      />

      {/* Render additional fields based on product type */}
      {PRODUCT_TYPES[formType]?.hasAdditionalFields &&
        PRODUCT_TYPES[formType].additionalFields.map(field => (
          <DynamicField
            key={field.type}
            fieldConfig={field}
            value={additionalFieldData[field.type] || ''}
            onChange={(e) => handleAdditionalFieldChange(field.type, e.target.value)}
            loading={loadingAdditionalFields[field.type]}
            options={additionalFieldOptions[field.type]}
          />
        ))
      }

      <div className="dome-d-flex dome-gap6 dome-flex-column">
        <div className="dome-p3 dome-text-w400">upload form*</div>
        {!file ? (
          <UploadArea inputChanged={addFile} exclude_icons={true}>
            <div className="dome-d-flex dome-gap12 dome-align-center">
              <div className="file-icons">
                <i className="fa-thin fa-cloud-arrow-up"></i>
              </div>
              <div className="dome-p3 dome-text-w400">
                <div>drop files here to upload,</div>
                <div>or click to browse.</div>
              </div>
            </div>
          </UploadArea>
        ) : (
          <FileDisplayArea file={file} onRemoveFile={removeFile} />
        )}
      </div>
      <div className="dome-d-flex dome-justify-between">
        <button
          type="button"
          onClick={hideOverlay}
          className="dome-btn dome-btn-link dome-btn-link-med-grey dome-btn-link-chevron-left"
        >
          back
        </button>

        <button
          type="submit"
          disabled={isSubmitDisabled() || loading}
          className={clsx('btn-base', isSubmitDisabled() ? 'btn-disabled' : 'btn-dark-green')}
        >
          {loading ? <i className="fa-duotone fa-spinner fa-spin-pulse"></i> : 'build form'}
        </button>
      </div>
    </form>
  )
}

export default CreatingFormBuilder
